import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { User, Settings, BarChart3, Image, FileText, Mail, Wrench, PlayCircle, UserCheck, BookOpen, MessageSquare, DollarSign, Layers, CheckCircle2 } from 'lucide-react';
import ParticlesBackground from '../components/ParticlesBackground';
import {
  DashboardContainer,
  WelcomeSection,
  WelcomeTitle,
  WelcomeText,
  CardsGrid,
  Card,
  CardIcon,
  CardContent,
  CardTitle,
  CardDescription,
  FloatingElement
} from './Dashboard.styles';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const dashboardCards = [
    {
      icon: Image,
      title: 'Hero Section',
      description: 'Manage your website hero section',
      color: '#C5337B',
      path: '/hero-section'
    },
    {
      icon: FileText,
      title: 'About Section',
      description: 'Manage your about page content',
      color: '#2B3857',
      path: '/about-section'
    },
    {
      icon: Wrench,
      title: 'Services Section',
      description: 'Manage your services and features',
      color: '#E91E63',
      path: '/services-section'
    },
    {
      icon: PlayCircle,
      title: 'How It Works Section',
      description: 'Manage your process and workflow',
      color: '#8B5CF6',
      path: '/how-it-works-section'
    },
    {
      icon: UserCheck,
      title: 'Our Team Section',
      description: 'Manage team member profiles',
      color: '#06B6D4',
      path: '/our-team-section'
    },
    {
      icon: BookOpen,
      title: 'Blogs Section',
      description: 'Manage blog posts and content',
      color: '#8B5CF6',
      path: '/blogs-section'
    },
    {
      icon: MessageSquare,
      title: 'Testimonials Section',
      description: 'Manage client testimonials',
      color: '#F59E0B',
      path: '/testimonials-section'
    },
    {
      icon: DollarSign,
      title: 'Pricing Section',
      description: 'Manage pricing packages and tiers',
      color: '#10B981',
      path: '/pricing-section'
    },
    {
      icon: Layers,
      title: 'Services Page',
      description: 'Manage detailed services page content',
      color: '#3B82F6',
      path: '/services-page'
    },
    {
      icon: Mail,
      title: 'Contact Section',
      description: 'Manage contact information',
      color: '#F06292',
      path: '/contact-section'
    },
    {
      icon: CheckCircle2,
      title: 'Content Approval',
      description: 'Review and approve pending content',
      color: '#10B981',
      path: '/approval',
      superAdminOnly: true
    },
    {
      icon: User,
      title: 'Users',
      description: 'Manage system users and permissions',
      color: '#64748B'
    },
    {
      icon: BarChart3,
      title: 'Analytics',
      description: 'View system statistics and reports',
      color: '#C5337B'
    },
    {
      icon: Settings,
      title: 'Settings',
      description: 'Configure your CMS preferences',
      color: '#2B3857'
    }
  ];

  return (
    <DashboardContainer>
      <ParticlesBackground />
      
      <FloatingElement delay="0s" />
      <FloatingElement delay="3s" />
      <FloatingElement delay="6s" />

      <WelcomeSection>
        <WelcomeTitle>Welcome to Your CMS</WelcomeTitle>
        <WelcomeText>
          Manage your website content, users, and analytics from this central dashboard.
          Use the sidebar to navigate between different sections.
        </WelcomeText>
      </WelcomeSection>

      <CardsGrid>
        {dashboardCards.map((card, index) => {
          // Skip super admin only cards if user is not super admin
          if (card.superAdminOnly && user?.role !== 'super_admin') {
            return null;
          }

          const IconComponent = card.icon;
          return (
            <Card 
              key={index} 
              delay={`${index * 0.1}s`} 
              color={card.color}
              onClick={() => card.path && navigate(card.path)}
              style={{ cursor: card.path ? 'pointer' : 'default' }}
            >
              <CardIcon color={card.color}>
                <IconComponent size={24} />
              </CardIcon>
              <CardContent>
                <CardTitle>{card.title}</CardTitle>
                <CardDescription>{card.description}</CardDescription>
              </CardContent>
            </Card>
          );
        })}
      </CardsGrid>
    </DashboardContainer>
  );
};

export default Dashboard;
